import {
  useIsMutating,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import { electronApis } from '@/features/electron-api';

import { siteCredentialsKey } from './site-credentials.api';

import type { ChromeImportResponse } from '../../../electron/shared/chrome-import.types';

const mutationKey = ['chrome-credential-import'];

export const useChromeCredentialImport = () => {
  const client = useQueryClient();
  const busy = useIsMutating({ mutationKey }) > 0;
  const mutation = useMutation({
    mutationKey,
    mutationFn: () => electronApis.chromeImport.importPasswords(),
    onSuccess: ({ kind, browser = 'Browser' }) => {
      const notices: Record<
        ChromeImportResponse['kind'],
        (() => unknown) | null
      > = {
        success: () => toast.success(`${browser} passwords imported`),
        partial: () =>
          toast.warning(
            'Some passwords couldn’t be imported. Try importing again.'
          ),
        empty: () =>
          toast.info(`No supported passwords found in this ${browser} profile`),
        unavailable: () =>
          toast.info('No supported browser data found on this device'),
        limit: () =>
          toast.warning(
            `${browser} profile exceeds the 3,000-entry import limit. Import from a smaller profile.`
          ),
        locked: () => toast.info(`Close ${browser}, then try importing again.`),
        error: () =>
          toast.error('Couldn’t finish importing passwords. Try again.'),
        cancelled: null,
      };
      notices[kind]?.();
    },
    onError: () =>
      toast.error('Couldn’t finish importing passwords. Try again.'),
    onSettled: (result) => {
      if (result?.kind === 'success' || result?.kind === 'partial')
        return client.invalidateQueries({ queryKey: siteCredentialsKey });
    },
  });
  return { mutate: mutation.mutate, busy };
};
